/**
 * Auto-calculated scoring for laadplein locations.
 * Each score is 1-10 with reasoning for tooltip display.
 */

export interface ScoreBreakdown {
  score: number;
  label: string;
  factors: { label: string; value: string; impact: number; tooltip: string }[];
}

function clamp(v: number, min = 1, max = 10): number {
  return Math.max(min, Math.min(max, Math.round(v * 10) / 10));
}

export function scoreBereikbaarheid(opts: {
  afstand_a_n_weg_km?: number | null;
  op_bedrijventerrein?: boolean | null;
  toegangsweg_breedte_m?: number | null;
}): ScoreBreakdown {
  const factors: ScoreBreakdown['factors'] = [];
  let base = 5;

  const d = opts.afstand_a_n_weg_km;
  if (d != null) {
    if (d < 0.5) base = 10;
    else if (d < 1) base = 8;
    else if (d < 2) base = 6;
    else if (d < 5) base = 4;
    else base = 2;
    factors.push({
      label: 'Afstand A/N-weg',
      value: `${d} km`,
      impact: base - 5,
      tooltip: 'SPULA vereist < 1 km. Dichter bij snelweg = meer passerend vrachtverkeer',
    });
  }

  if (opts.op_bedrijventerrein) {
    base += 1;
    factors.push({
      label: 'Bedrijventerrein',
      value: 'Ja',
      impact: 1,
      tooltip: 'Op bedrijventerrein = betere bereikbaarheid voor vrachtwagens en SPULA-eligible',
    });
  }

  const bw = opts.toegangsweg_breedte_m;
  if (bw != null) {
    const bonus = bw >= 6 ? 1 : bw < 4 ? -2 : 0;
    if (bonus !== 0) {
      base += bonus;
      factors.push({
        label: 'Wegbreedte',
        value: `${bw} m`,
        impact: bonus,
        tooltip: 'Minimaal 6m verhard nodig voor vrachtwagens (SPULA-eis)',
      });
    }
  }

  return { score: clamp(base), label: 'Bereikbaarheid', factors };
}

export function scoreNetcapaciteit(opts: {
  netaansluiting_kw?: number | null;
  netcongestie_status?: string | null;
}): ScoreBreakdown {
  const factors: ScoreBreakdown['factors'] = [];
  let base = 5;

  const kw = opts.netaansluiting_kw;
  if (kw != null) {
    if (kw >= 1750) base = 10;
    else if (kw >= 1000) base = 8;
    else if (kw >= 630) base = 6;
    else if (kw >= 160) base = 4;
    else base = 2;
    factors.push({
      label: 'Netaansluiting',
      value: `${kw} kW`,
      impact: base - 5,
      tooltip: '1750 kW+ = geschikt voor 4+ truck HPC (350kW). 100kW minimum voor SPULA.',
    });
  }

  const status = opts.netcongestie_status;
  if (status && status !== 'onbekend') {
    const penalty = status === 'geen' ? 0 : status === 'licht' ? -1 : -3;
    if (penalty !== 0) {
      base += penalty;
      factors.push({
        label: 'Netcongestie',
        value: status,
        impact: penalty,
        tooltip: status === 'zwaar'
          ? 'Zware congestie: extra investering in batterij/nettransformator nodig'
          : 'Lichte congestie: mogelijk vertraging bij netaansluiting',
      });
    }
  }

  return { score: clamp(base), label: 'Netcapaciteit', factors };
}

export function scoreConcurrentie(opts: {
  concurrenten_10km?: number;
  truck_hpc_25km?: number;
  spula_resterend?: number;
}): ScoreBreakdown {
  const factors: ScoreBreakdown['factors'] = [];
  let base = 5;

  const thpc = opts.truck_hpc_25km ?? 0;
  if (thpc === 0) base = 10;
  else if (thpc === 1) base = 8;
  else if (thpc <= 3) base = 6;
  else base = 4;
  factors.push({
    label: 'Truck HPC binnen 25km',
    value: `${thpc}`,
    impact: base - 5,
    tooltip: 'Minder truck HPC concurrenten = meer marktpotentieel',
  });

  const c10 = opts.concurrenten_10km ?? 0;
  if (c10 > 5) {
    base -= 1;
    factors.push({
      label: 'Totaal concurrenten <10km',
      value: `${c10}`,
      impact: -1,
      tooltip: 'Hoge dichtheid van laadpunten in directe omgeving',
    });
  }

  if (opts.spula_resterend != null && opts.spula_resterend >= 3) {
    base += 1;
    factors.push({
      label: 'SPULA slots beschikbaar',
      value: `${opts.spula_resterend} resterend`,
      impact: 1,
      tooltip: 'Nog voldoende SPULA-subsidie beschikbaar in dit postcodegebied',
    });
  }

  return { score: clamp(base), label: 'Concurrentie', factors };
}

export function scoreVraag(opts: {
  trucks_per_dag?: number;
  in_ze_zone?: boolean;
  transporter_leads?: number;
}): ScoreBreakdown {
  const factors: ScoreBreakdown['factors'] = [];
  let base = 5;

  const t = opts.trucks_per_dag ?? 0;
  if (t > 5000) base = 10;
  else if (t > 3000) base = 8;
  else if (t > 1500) base = 6;
  else if (t > 500) base = 4;
  else if (t > 0) base = 2;
  factors.push({
    label: 'Vrachtverkeer/dag',
    value: t > 0 ? `${t.toLocaleString()}` : 'Onbekend',
    impact: base - 5,
    tooltip: 'Bron: NDW verkeersdata nabije corridor. Meer trucks = meer potentiele klanten.',
  });

  if (opts.in_ze_zone) {
    base += 1;
    factors.push({
      label: 'Zero-emissie zone',
      value: 'Nabij ZE-zone',
      impact: 1,
      tooltip: 'Locatie bedient ZE-zone stad. Verplichte elektrificatie creert extra vraag.',
    });
  }

  if (opts.transporter_leads && opts.transporter_leads > 0) {
    base += 1;
    factors.push({
      label: 'Transporter leads',
      value: `${opts.transporter_leads}`,
      impact: 1,
      tooltip: 'Geinteresseerde transporteurs in het gebied',
    });
  }

  return { score: clamp(base), label: 'Vraag', factors };
}

export function scoreKosten(opts: {
  huur_koop?: string | null;
  oppervlakte_m2?: number | null;
  netcongestie_status?: string | null;
}): ScoreBreakdown {
  const factors: ScoreBreakdown['factors'] = [];
  let base = 5;

  const hk = opts.huur_koop;
  if (hk) {
    const map: Record<string, number> = { eigen: 10, koop: 7, optie: 6, huur: 5 };
    base = map[hk] ?? 5;
    factors.push({
      label: 'Eigendomssituatie',
      value: hk,
      impact: base - 5,
      tooltip: 'Eigen grond = laagste kosten. Huur = terugkerende lasten, minder controle.',
    });
  }

  const opp = opts.oppervlakte_m2;
  if (opp != null) {
    const bonus = opp >= 5000 ? 1 : opp < 1500 ? -1 : 0;
    if (bonus !== 0) {
      base += bonus;
      factors.push({
        label: 'Oppervlakte',
        value: `${opp.toLocaleString()} m²`,
        impact: bonus,
        tooltip: bonus > 0 ? 'Groot terrein = ruimte voor uitbreiding' : 'Klein terrein = beperkte capaciteit',
      });
    }
  }

  if (opts.netcongestie_status === 'zwaar') {
    base -= 2;
    factors.push({
      label: 'Congestie investering',
      value: 'Zwaar',
      impact: -2,
      tooltip: 'Zware netcongestie vereist extra investering in batterijopslag of netuitbreiding',
    });
  }

  return { score: clamp(base), label: 'Kosten', factors };
}

export interface FullScoreResult {
  bereikbaarheid: ScoreBreakdown;
  netcapaciteit: ScoreBreakdown;
  concurrentie: ScoreBreakdown;
  vraag: ScoreBreakdown;
  kosten: ScoreBreakdown;
  totaal: number;
}

export function calculateFullScore(opts: {
  afstand_a_n_weg_km?: number | null;
  op_bedrijventerrein?: boolean | null;
  toegangsweg_breedte_m?: number | null;
  netaansluiting_kw?: number | null;
  netcongestie_status?: string | null;
  concurrenten_10km?: number;
  truck_hpc_25km?: number;
  spula_resterend?: number;
  trucks_per_dag?: number;
  in_ze_zone?: boolean;
  transporter_leads?: number;
  huur_koop?: string | null;
  oppervlakte_m2?: number | null;
}): FullScoreResult {
  const b = scoreBereikbaarheid(opts);
  const n = scoreNetcapaciteit(opts);
  const c = scoreConcurrentie(opts);
  const v = scoreVraag(opts);
  const k = scoreKosten(opts);
  const totaal = Math.round(((b.score + n.score + c.score + v.score + k.score) / 5) * 10) / 10;

  return {
    bereikbaarheid: b,
    netcapaciteit: n,
    concurrentie: c,
    vraag: v,
    kosten: k,
    totaal,
  };
}
